import z from "zod";

import { Schemas } from "../../schemas";
import type { Kpi, ServiceTemplate } from "../../types";
import { kpiService } from "./kpiService";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";

const API_PATH = "/v1/service-template";
const schema = z.array(Schemas.serviceTemplate);

const loadAll = async (): Promise<ServiceTemplate[]> =>
  schema.parse(await sigv4ApiClient.list<ServiceTemplate>(API_PATH));

const saveOne = async (template: ServiceTemplate): Promise<ServiceTemplate> =>
  sigv4ApiClient.put<ServiceTemplate>(API_PATH, template);

const isKpi = (value: Kpi | undefined): value is Kpi => Boolean(value);

export const serviceTemplateService = {
  list: async (): Promise<ServiceTemplate[]> => {
    return loadAll();
  },
  create: async (input: {
    name: string;
    description: string;
    type: ServiceTemplate["type"];
    kpiIds: string[];
  }): Promise<ServiceTemplate> => {
    const id = createEntityId();

    const allKpis = await kpiService.list();
    const kpiById = new Map(allKpis.map((kpi) => [kpi.id, kpi]));
    const selectedKpis = Array.from(new Set(input.kpiIds))
      .map((kpiId) => kpiById.get(kpiId))
      .filter(isKpi);

    if (selectedKpis.length === 0) {
      throw new Error("Select at least one KPI for the template.");
    }

    const created: ServiceTemplate = Schemas.serviceTemplate.parse({
      id,
      name: input.name.trim(),
      system: false,
      description: input.description.trim(),
      type: input.type,
      kpis: selectedKpis,
    });

    return saveOne(created);
  },
  get: async (id: string): Promise<ServiceTemplate | undefined> => {
    return (await serviceTemplateService.list()).find((template) => template.id === id);
  },
  patch: async (
    id: string,
    patch: Partial<{
      name: string;
      description: string;
      type: ServiceTemplate["type"];
      kpiIds: string[];
    }>,
  ): Promise<ServiceTemplate> => {
    const templates = await loadAll();
    const idx = templates.findIndex((template) => template.id === id);
    if (idx === -1) {
      throw new Error(`Service template with id "${id}" not found`);
    }

    const current = templates[idx];
    if (current.system) {
      throw new Error("System service templates cannot be edited.");
    }

    let resolvedKpis = current.kpis;

    if (patch.kpiIds) {
      const allKpis = await kpiService.list();
      const kpiById = new Map(allKpis.map((kpi) => [kpi.id, kpi]));
      const selectedKpis = Array.from(new Set(patch.kpiIds))
        .map((kpiId) => kpiById.get(kpiId))
        .filter(isKpi);

      if (selectedKpis.length === 0) {
        throw new Error("Select at least one KPI for the template.");
      }

      resolvedKpis = selectedKpis;
    }

    const updated = Schemas.serviceTemplate.parse({
      ...current,
      id,
      name: patch.name?.trim() ?? current.name,
      description: patch.description?.trim() ?? current.description,
      type: patch.type ?? current.type,
      kpis: resolvedKpis,
    });

    return saveOne(updated);
  },
  delete: async (id: string): Promise<boolean> => {
    const template = await serviceTemplateService.get(id);
    if (!template) {
      return false;
    }

    if (template.system) {
      return false;
    }

    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(id)}`);
    return true;
  },
  isDefaultId: (): boolean => {
    return false;
  },
};
